import { useEffect, useState } from "react";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable } from "../../components/common/DataTable";
import { FormModal } from "../../components/common/FormModal";
import { StatusBadge } from "../../components/common/StatusBadge";
import { TextField, SelectField } from "../../components/common/Form";
import { Button } from "@/components/ui/button";
import {
  BottleProductsService,
  BottleTypesService,
  BottleCompaniesService,
} from "../../services";
import { currency } from "../../utils/format";
import { getBarProductTypeLabel } from "../../utils/barProducts";
import { ImageOff, ImagePlus, Pencil, Plus, Power } from "lucide-react";
import { toast } from "sonner";
import { useCan } from "../../hooks/useCan";

const empty = {
  barProductType: "LIQUOR",
  bottleTypeId: "",
  companyId: "",
  bottleName: "",
  bottleSize: "BOTTLE",
  barcode: "",
  volumeMl: "",
  sellingPrice: 0,
  lowStockLevel: 0,

  isReturnableBottle: false,
  bottleReturnCredit: "",

  allowMeasureSale: false,
  measure180Price: "",
  measure90Price: "",
};

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

const formatImageFileSize = (size) => {
  if (!size) {
    return "";
  }

  if (size < 1024 * 1024) {
    return `${Math.round(size / 1024)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const ProductImagePicker = ({
  previewUrl,
  selectedFile,
  existingImageUrl,
  markedForRemoval,
  onSelectFile,
  onClearSelectedFile,
  onRemoveExistingImage,
  disabled,
}) => (
  <div className="md:col-span-2 rounded-xl border border-slate-200 bg-white p-3">
    <div className="flex flex-col md:flex-row gap-4">
      <div className="h-32 w-full md:w-40 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden grid place-items-center shrink-0">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Bottle product preview"
            className="h-full w-full object-contain p-2"
          />
        ) : (
          <div className="text-center text-slate-400 px-3">
            <ImageOff className="h-8 w-8 mx-auto mb-1" />
            <div className="text-xs font-medium">No image</div>
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800">
          Product image
        </div>

        <p className="text-xs text-slate-500 mt-1">
          Select an image from your computer. You can preview it before saving.
          JPG, PNG and WEBP are supported up to 10 MB.
        </p>

        {selectedFile && (
          <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-xs text-emerald-700">
            Selected: {selectedFile.name} {formatImageFileSize(selectedFile.size) && `(${formatImageFileSize(selectedFile.size)})`}
          </div>
        )}

        {!selectedFile && existingImageUrl && !markedForRemoval && (
          <div className="mt-2 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2 text-xs text-sky-700">
            Current saved image will be kept unless you replace or remove it.
          </div>
        )}

        {markedForRemoval && (
          <div className="mt-2 rounded-lg bg-rose-50 border border-rose-100 px-3 py-2 text-xs text-rose-700">
            Current image will be removed when you save.
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 rounded-md bg-navy-800 hover:bg-navy-900 text-white text-xs font-semibold px-3 py-2 cursor-pointer">
            <ImagePlus className="h-4 w-4" />
            Select Image
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              className="hidden"
              disabled={disabled}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                onSelectFile(file);
              }}
            />
          </label>

          {selectedFile && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClearSelectedFile}
              disabled={disabled}
            >
              Clear Selected
            </Button>
          )}

          {!selectedFile && existingImageUrl && !markedForRemoval && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRemoveExistingImage}
              disabled={disabled}
              className="text-rose-700 hover:text-rose-800"
            >
              Remove Image
            </Button>
          )}
        </div>
      </div>
    </div>
  </div>
);

export const BottleProductsPage = () => {
  const can = useCan();

  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [removeImage, setRemoveImage] = useState(false);

  const load = () => {
    setLoading(true);

    Promise.all([
      BottleProductsService.list(),
      BottleTypesService.list(),
      BottleCompaniesService.list(),
    ])
      .then(([p, t, c]) => {
        setRows([...p].sort((a, b) => a.bottleId - b.bottleId));
        setTypes(
          [...t].sort((a, b) => a.bottleTypeId - b.bottleTypeId)
        );
        setCompanies(
          [...c].sort((a, b) => a.companyId - b.companyId)
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const openCreate = () => {
    setForm({ ...empty });
    setEditingId(null);
    setErrors({});
    setImageFile(null);
    setImagePreviewUrl("");
    setExistingImageUrl("");
    setRemoveImage(false);
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.bottleId);

    setForm({
      barProductType: r.barProductType || "LIQUOR",
      bottleTypeId: r.bottleTypeId,
      companyId: r.companyId,
      bottleName: r.bottleName,
      bottleSize: r.bottleSize || "BOTTLE",
      barcode: r.barcode || "",
      volumeMl: r.volumeMl ?? "",
      sellingPrice: r.sellingPrice,
      lowStockLevel: r.lowStockLevel,

      isReturnableBottle: Boolean(r.isReturnableBottle),
      bottleReturnCredit: r.bottleReturnCredit ?? "",

      allowMeasureSale: Boolean(r.allowMeasureSale),
      measure180Price: r.measure180Price ?? "",
      measure90Price: r.measure90Price ?? "",
    });

    setErrors({});
    setImageFile(null);
    setImagePreviewUrl(r.imageUrl || "");
    setExistingImageUrl(r.imageUrl || "");
    setRemoveImage(false);
    setOpen(true);
  };

  const handleModalOpenChange = (nextOpen) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      setImageFile(null);
      setImagePreviewUrl("");
      setExistingImageUrl("");
      setRemoveImage(false);
    }
  };

  const handleImageFileSelect = (file) => {
    if (!file) {
      return;
    }

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error("Only JPG, JPEG, PNG and WEBP images are allowed");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error("Image file size must not exceed 10 MB");
      return;
    }

    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
    setRemoveImage(false);
  };

  const clearSelectedImageFile = () => {
    setImageFile(null);
    setImagePreviewUrl(existingImageUrl || "");
    setRemoveImage(false);
  };

  const markExistingImageForRemoval = () => {
    setImageFile(null);
    setImagePreviewUrl("");
    setRemoveImage(Boolean(existingImageUrl));
  };

  const resolveSavedBottleId = (response) => {
    return (
      editingId ||
      response?.bottleId ||
      response?.data?.bottleId ||
      response?.data?.data?.bottleId ||
      response?.data?.data?.data?.bottleId ||
      null
    );
  };

  const validate = () => {
    const e = {};

    if (!form.bottleTypeId) {
      e.bottleTypeId = "Required";
    }

    if (!form.companyId) {
      e.companyId = "Required";
    }

    if (!form.bottleName?.trim()) {
      e.bottleName = "Required";
    }

    if (form.barProductType === "LIQUOR" && !form.bottleSize?.trim()) {
      e.bottleSize = "Required";
    }

    if (form.barcode && form.barcode.trim().length > 100) {
      e.barcode = "Barcode cannot exceed 100 characters";
    }

    if (
      form.barProductType === "LIQUOR" && (form.volumeMl === "" ||
      form.volumeMl == null ||
      Number(form.volumeMl) <= 0)
    ) {
      e.volumeMl = "Must be greater than 0";
    }

    if (
      form.sellingPrice == null ||
      Number(form.sellingPrice) < 0
    ) {
      e.sellingPrice = "Must be ≥ 0";
    }

    if (
      form.lowStockLevel == null ||
      Number(form.lowStockLevel) < 0
    ) {
      e.lowStockLevel = "Must be ≥ 0";
    }

    if (form.barProductType === "LIQUOR" && form.isReturnableBottle) {
      if (
        form.bottleReturnCredit === "" ||
        form.bottleReturnCredit == null
      ) {
        e.bottleReturnCredit = "Required";
      } else if (Number(form.bottleReturnCredit) <= 0) {
        e.bottleReturnCredit = "Must be greater than 0";
      }
    }

    if (form.barProductType === "LIQUOR" && form.allowMeasureSale) {
      if (Number(form.volumeMl) !== 750) {
        e.allowMeasureSale =
          "Measured sales are allowed only for 750 ml bottles";
      }

      if (
        form.measure180Price === "" ||
        form.measure180Price == null
      ) {
        e.measure180Price = "Required";
      } else if (Number(form.measure180Price) <= 0) {
        e.measure180Price = "Must be greater than 0";
      }

      if (
        form.measure90Price === "" ||
        form.measure90Price == null
      ) {
        e.measure90Price = "Required";
      } else if (Number(form.measure90Price) <= 0) {
        e.measure90Price = "Must be greater than 0";
      }
    }

    setErrors(e);

    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);

    const measuredSaleEnabled =
      Number(form.volumeMl) === 750 &&
      Boolean(form.allowMeasureSale);

    const payload = {
      bottleTypeId: Number(form.bottleTypeId),
      companyId: Number(form.companyId),
      bottleName: form.bottleName.trim(),
      barProductType: form.barProductType,
      bottleSize: form.barProductType === "LIQUOR" ? form.bottleSize : null,
      barcode: form.barcode?.trim() || null,
      volumeMl: form.barProductType === "LIQUOR" ? Number(form.volumeMl) : null,
      sellingPrice: Number(form.sellingPrice),
      lowStockLevel: Number(form.lowStockLevel),

      isReturnableBottle: form.barProductType === "LIQUOR" && Boolean(form.isReturnableBottle),
      bottleReturnCredit: form.barProductType === "LIQUOR" && form.isReturnableBottle
        ? Number(form.bottleReturnCredit)
        : null,

      allowMeasureSale: form.barProductType === "LIQUOR" && measuredSaleEnabled,
      measure180Price: measuredSaleEnabled
        ? Number(form.measure180Price)
        : null,
      measure90Price: measuredSaleEnabled
        ? Number(form.measure90Price)
        : null,
    };

    try {
      const savedProduct = editingId
        ? await BottleProductsService.update(editingId, payload)
        : await BottleProductsService.create(payload);

      const targetBottleId = resolveSavedBottleId(savedProduct);

      if (imageFile) {
        if (!targetBottleId) {
          toast.warning(
            "Bottle saved, but image was not uploaded because the saved bottle ID was not returned."
          );
        } else {
          await BottleProductsService.uploadImage(targetBottleId, imageFile);
        }
      } else if (removeImage && targetBottleId) {
        await BottleProductsService.removeImage(targetBottleId);
      }

      toast.success(imageFile ? "Saved with image" : "Saved");
      setOpen(false);
      setImageFile(null);
      setImagePreviewUrl("");
      setExistingImageUrl("");
      setRemoveImage(false);
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Failed"
      );
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (r) => {
    try {
      await BottleProductsService.setStatus(
        r.bottleId,
        r.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"
      );

      load();
      toast.success("Status updated");
    } catch (e) {
      toast.error(
        e?.response?.data?.message || "Failed"
      );
    }
  };

  const columns = [
    {
      key: "barProductType",
      header: "Product Type",
      render: (r) => getBarProductTypeLabel(r),
    },
    {
      key: "bottleName",
      header: "Name",
    },
    {
      key: "bottleTypeName",
      header: "Type",
    },
    {
      key: "companyName",
      header: "Company",
    },
    {
      key: "bottleSize",
      header: "Container",
      render: (r) =>
        r.barProductType === "CIGARETTE" ? "—" : r.bottleSize === "TIN" ? "Tin" : "Bottle",
    },
    {
      key: "volumeMl",
      header: "Volume",
      render: (r) =>
        r.barProductType === "CIGARETTE" ? "Units" : r.volumeMl ? `${r.volumeMl} ML` : "—",
    },
    {
      key: "sellingPrice",
      header: "Selling Price",
      render: (r) => currency(r.sellingPrice),
    },
    {
      key: "returnable",
      header: "Returnable",
      render: (r) =>
        r.isReturnableBottle ? (
          <div className="text-xs">
            <div className="font-medium text-emerald-700">
              Yes
            </div>

            <div className="text-slate-500">
              Credit: {currency(r.bottleReturnCredit)}
            </div>
          </div>
        ) : (
          <span className="text-slate-400 text-xs">
            No
          </span>
        ),
    },
    {
      key: "measuredSale",
      header: "Measured Sale",
      render: (r) =>
        r.allowMeasureSale ? (
          <div className="text-xs">
            <div className="font-medium text-amber-700">
              Enabled
            </div>

            <div className="text-slate-500">
              180 ml: {currency(r.measure180Price)}
            </div>

            <div className="text-slate-500">
              90 ml: {currency(r.measure90Price)}
            </div>
          </div>
        ) : (
          <span className="text-slate-400 text-xs">
            No
          </span>
        ),
    },
    {
      key: "availableQuantity",
      header: "Stock",
      render: (r) => (
        <span
          className={
            r.availableQuantity <= r.lowStockLevel
              ? "text-rose-600 font-medium"
              : ""
          }
        >
          {r.availableQuantity ?? 0}
        </span>
      ),
    },
    ...(can.isAdmin
      ? [
        {
          key: "lowStockLevel",
          header: "Low at",
        },
      ]
      : []),
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <StatusBadge value={r.status} />
      ),
    },
    {
      key: "actions",
      header: "",
      render: (r) =>
        can.modify ? (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => openEdit(r)}
              data-testid={`bottle-edit-${r.bottleId}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => toggle(r)}
            >
              <Power className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <span className="text-slate-300 text-xs">
            —
          </span>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="BAR Products"
        subtitle="BAR product catalog with selling price and low-stock level."
        actions={
          can.create && (
            <Button
              onClick={openCreate}
              className="bg-navy-800 hover:bg-navy-900 text-white"
              data-testid="bottles-add"
            >
              <Plus className="h-4 w-4 mr-2" />
              New BAR Product
            </Button>
          )
        }
      />

      <DataTable
        loading={loading}
        rows={rows.map((r) => ({
          ...r,
          key: r.bottleId,
        }))}
        testId="bottles-table"
        searchable
        searchKeys={[
          "bottleName",
          "bottleTypeName",
          "companyName",
          "bottleSize",
          "barcode",
          "status",
        ]}
        searchPlaceholder="Search bottles by name, type, company or container…"
        columns={columns}
      />

      <FormModal
        open={open}
        onOpenChange={handleModalOpenChange}
        title={editingId ? "Edit BAR product" : "New BAR product"}
        onSubmit={submit}
        loading={saving}
        size="lg"
      >
        <div className="max-h-[72vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ProductImagePicker
              previewUrl={imagePreviewUrl}
              selectedFile={imageFile}
              existingImageUrl={existingImageUrl}
              markedForRemoval={removeImage}
              onSelectFile={handleImageFileSelect}
              onClearSelectedFile={clearSelectedImageFile}
              onRemoveExistingImage={markExistingImageForRemoval}
              disabled={saving}
            />

          <SelectField
            label="Product category"
            required
            value={form.bottleTypeId}
            onChange={(v) =>
              setForm({
                ...form,
                bottleTypeId: v,
              })
            }
            options={types.map((t) => ({
              value: t.bottleTypeId,
              label: t.typeName,
            }))}
            error={errors.bottleTypeId}
            testId="bottle-type"
          />

          <SelectField
            label="Product type"
            required
            value={form.barProductType}
            onChange={(v) => setForm({
              ...form,
              barProductType: v,
              bottleSize: v === "LIQUOR" ? (form.bottleSize || "BOTTLE") : "",
              volumeMl: v === "LIQUOR" ? form.volumeMl : "",
              allowMeasureSale: false,
              isReturnableBottle: false,
              bottleReturnCredit: "",
              measure180Price: "",
              measure90Price: "",
            })}
            options={[
              { value: "LIQUOR", label: "Liquor" },
              { value: "CIGARETTE", label: "Cigarette" },
            ]}
            testId="bar-product-type"
          />

          <SelectField
            label="Company"
            required
            value={form.companyId}
            onChange={(v) =>
              setForm({
                ...form,
                companyId: v,
              })
            }
            options={companies.map((c) => ({
              value: c.companyId,
              label: c.companyName,
            }))}
            error={errors.companyId}
            testId="bottle-company"
          />

          <TextField
            label="Product name"
            required
            value={form.bottleName}
            onChange={(v) =>
              setForm({
                ...form,
                bottleName: v,
              })
            }
            error={errors.bottleName}
            testId="bottle-name"
          />

          <TextField
            label="Barcode"
            value={form.barcode}
            onChange={(v) =>
              setForm({
                ...form,
                barcode: v,
              })
            }
            error={errors.barcode}
            testId="bottle-barcode"
          />

          {form.barProductType === "LIQUOR" && <>
          <SelectField
            label="Container type"
            required
            value={form.bottleSize}
            onChange={(v) =>
              setForm({
                ...form,
                bottleSize: v,
              })
            }
            options={[
              {
                value: "BOTTLE",
                label: "Bottle",
              },
              {
                value: "TIN",
                label: "Tin",
              },
            ]}
            error={errors.bottleSize}
            testId="bottle-container-type"
          />

          <TextField
            label="Bottle volume (ml)"
            required
            type="number"
            value={form.volumeMl}
            onChange={(v) => {
              const is750Ml = Number(v) === 750;

              setForm({
                ...form,
                volumeMl: v,
                allowMeasureSale: is750Ml
                  ? form.allowMeasureSale
                  : false,
                measure180Price: is750Ml
                  ? form.measure180Price
                  : "",
                measure90Price: is750Ml
                  ? form.measure90Price
                  : "",
              });
            }}
            error={errors.volumeMl}
            testId="bottle-volume"
          />
          </>}

          <TextField
            label="Selling price"
            required
            type="number"
            value={form.sellingPrice}
            onChange={(v) =>
              setForm({
                ...form,
                sellingPrice: v,
              })
            }
            error={errors.sellingPrice}
            testId="bottle-price"
          />

          <TextField
            label="Low stock level"
            required
            type="number"
            value={form.lowStockLevel}
            onChange={(v) =>
              setForm({
                ...form,
                lowStockLevel: v,
              })
            }
            error={errors.lowStockLevel}
            testId="bottle-low"
          />

          {form.barProductType === "LIQUOR" && <><div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={form.isReturnableBottle}
                onChange={(e) => {
                  const checked = e.target.checked;

                  setForm({
                    ...form,
                    isReturnableBottle: checked,
                    bottleReturnCredit: checked
                      ? form.bottleReturnCredit
                      : "",

                  });
                }}
                data-testid="bottle-returnable"
              />

              Returnable bottle product
            </label>

            <p className="text-xs text-slate-500 mt-1">
              Enables a credit for each empty bottle returned by the customer.
            </p>
          </div>

          {form.isReturnableBottle && (
            <TextField
              label="Credit per returned bottle"
              required
              type="number"
              value={form.bottleReturnCredit}
              onChange={(v) =>
                setForm({
                  ...form,
                  bottleReturnCredit: v,
                })
              }
              error={errors.bottleReturnCredit}
              testId="bottle-return-credit"
            />
          )}

          {Number(form.volumeMl) === 750 && (
              <div className="md:col-span-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.allowMeasureSale}
                    onChange={(e) => {
                      const checked = e.target.checked;

                      setForm({
                        ...form,
                        allowMeasureSale: checked,
                        measure180Price: checked
                          ? form.measure180Price
                          : "",
                        measure90Price: checked
                          ? form.measure90Price
                          : "",
                      });
                    }}
                    data-testid="bottle-allow-measure-sale"
                  />

                  Allow measured sales
                </label>

                <p className="text-xs text-slate-500 mt-1">
                  Allows this 750 ml bottle to be opened and
                  sold as 180 ml or 90 ml servings.
                </p>

                {errors.allowMeasureSale && (
                  <p className="text-xs text-rose-600 mt-1">
                    {errors.allowMeasureSale}
                  </p>
                )}
              </div>
            )}

          {Number(form.volumeMl) === 750 &&
            form.allowMeasureSale && (
              <>
                <TextField
                  label="180 ml selling price"
                  required
                  type="number"
                  value={form.measure180Price}
                  onChange={(v) =>
                    setForm({
                      ...form,
                      measure180Price: v,
                    })
                  }
                  error={errors.measure180Price}
                  testId="bottle-measure-180-price"
                />

                <TextField
                  label="90 ml selling price"
                  required
                  type="number"
                  value={form.measure90Price}
                  onChange={(v) =>
                    setForm({
                      ...form,
                      measure90Price: v,
                    })
                  }
                  error={errors.measure90Price}
                  testId="bottle-measure-90-price"
                />
              </>
            )}</>}
          </div>
        </div>
      </FormModal>
    </div>
  );
};
